import React, { useState, useRef, useEffect } from 'react';
import { X, ExternalLink, TrendingUp, BookOpen, Send, Sparkles, RotateCcw, AlertTriangle, ShieldCheck, Key } from 'lucide-react';
import { createRiskSummary } from '../utils/riskInterpreter';
import APIKeyModal, { getStoredApiKey } from './APIKeyModal';
import MarkdownRenderer from './MarkdownRenderer';

const AI_PROXY_URL = 'https://us-central1-geneie513.cloudfunctions.net/anthropicProxy';

// ─── Genotype badge ───────────────────────────────────────────────────────────
const GenotypeBadge = ({ copies }) => {
  if (copies === null || copies === undefined) return null;
  if (copies === 0) return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-50 border border-green-200 rounded text-xs text-green-700 font-medium">
      <ShieldCheck className="w-3 h-3" /> Not carried
    </span>
  );
  if (copies === 1) return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 font-medium">
      <AlertTriangle className="w-3 h-3" /> 1 copy
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-50 border border-red-200 rounded text-xs text-red-700 font-medium">
      <AlertTriangle className="w-3 h-3" /> 2 copies
    </span>
  );
};

// ─── Direction badge ──────────────────────────────────────────────────────────
const isQuantitativeTrait = (trait) => {
  if (!trait) return false;
  const lower = trait.toLowerCase();
  return [
    'level', 'levels', 'measurement', 'concentration', 'size', 'height', 'weight',
    'length', 'area', 'volume', 'count', 'number', 'ratio', 'rate', 'speed', 'age',
    'duration', 'time', 'density', 'pressure', 'capacity', 'score', 'index',
    'cholesterol', 'glucose', 'triglyceride', 'insulin', 'bmi', 'body mass',
    'adiposity', 'phospholipid', 'lipid', 'lipoprotein', 'hdl', 'ldl', 'vldl',
    'albumin', 'creatinine', 'urate', 'uric acid', 'magnesium', 'calcium',
    'potassium', 'sodium', 'iron', 'ferritin', 'haemoglobin', 'hemoglobin',
    'platelet', 'white blood cell', 'red blood cell', 'leucocyte', 'lymphocyte',
    'neutrophil', 'testosterone', 'cortisol', 'thyroid', 'tsh', 'vitamin',
    'c-reactive protein', 'crp', 'interleukin', 'forced expiratory', 'fev', 'fvc',
    'lung function', 'pulmonary function', 'blood pressure', 'systolic', 'diastolic',
    'heart rate', 'waist', 'hip circumference', 'fat mass', 'lean mass',
    'bone mineral', 'grip strength', 'walking speed', 'intelligence', 'cognitive',
    'educational attainment', 'years of schooling',
  ].some(kw => lower.includes(kw));
};

const DirectionBadge = ({ direction, userEffectiveOdds, orBeta, trait }) => {
  const val = userEffectiveOdds || orBeta;
  if (!direction || !val) return null;
  const isQuant = isQuantitativeTrait(trait);
  const isBeta = isQuant && (val < 0.5 || val > 2.0);
  if (isBeta) {
    return direction === 'increased'
      ? <span className="text-xs text-red-600 font-medium">higher</span>
      : <span className="text-xs text-green-600 font-medium">lower</span>;
  }
  const pct = Math.abs(((val - 1) * 100)).toFixed(0);
  return direction === 'increased'
    ? <span className="text-xs text-red-600 font-medium">{pct}% higher odds</span>
    : <span className="text-xs text-green-600 font-medium">{pct}% lower odds</span>;
};

// ─── AI Chat Panel ────────────────────────────────────────────────────────────
const AIChatPanel = ({ snp, apiKey }) => {
  const initialPrompt = 'Tell me about the ' + snp.gene + ' gene and what ' + snp.rsid + ' means for my health.';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(initialPrompt);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const prevMsgCountRef = useRef(0);

  useEffect(() => {
    // Only scroll when a new message is added, not on loading state changes
    if (messages.length > prevMsgCountRef.current) {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    prevMsgCountRef.current = messages.length;
  }, [messages.length]);

  const sendToAPI = async (currentMessages) => {
    setLoading(true);
    try {
      const riskSummary = createRiskSummary(snp);

      const gwasLines = snp.gwas?.topAssociations?.length
        ? snp.gwas.topAssociations.slice(0, 3).map(a =>
            '  - ' + a.trait +
            (a.copiesCarried !== null ? ' (user carries ' + a.copiesCarried + ' copy/copies of ' + a.allele + ')' : '') +
            (a.direction ? ', ' + a.direction + ' risk' : '')
          )
        : [];

      const systemLines = [
        'You are a genetics education assistant. Answer clearly for a non-specialist.',
        'Be concise (2-4 paragraphs). Do not provide medical advice.',
        '',
        'Current SNP:',
        '- rsID: ' + snp.rsid,
        '- Gene: ' + snp.gene,
        '- User genotype: ' + snp.genotype,
        '- Clinical significance: ' + (snp.clinicalSignificance || 'not specified'),
        '- Conditions: ' + (snp.conditions || 'none listed'),
        '- Risk summary: ' + riskSummary.primaryMessage,
      ];
      if (gwasLines.length > 0) {
        systemLines.push('', 'GWAS associations for this user:');
        gwasLines.forEach(l => systemLines.push(l));
      }

      // Cap history at last 6 messages to control token costs
      const recentMessages = currentMessages.slice(-6).map(m => ({
        role: m.role === 'assistant' ? 'assistant' : 'user',
        content: m.text,
      }));

      // Route through Firebase proxy to avoid CORS block on api.anthropic.com
      const response = await fetch(AI_PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 600,
          system: systemLines.join('\n'),
          messages: recentMessages,
        }),
      });

      const data = await response.json();
      const replyText = (data.content || [])
        .filter(b => b.type === 'text')
        .map(b => b.text)
        .join('\n')
        .trim();

      if (replyText) {
        setMessages(prev => [...prev, { role: 'assistant', text: replyText }]);
      }
    } catch (err) {
      console.error('AI chat error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Sorry, I was not able to fetch that right now. Try again or check the external links below.',
      }]);
    }
    setLoading(false);
  };

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    const next = [...messages, { role: 'user', text: trimmed }];
    setMessages(next);
    setInput('');
    sendToAPI(next);
  };

  return (
    <div className="flex flex-col border border-gray-200 rounded-xl overflow-hidden bg-gray-50" style={{ height: '420px' }}>
      <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-600" />
          <span className="text-sm font-semibold text-gray-800">Ask about {snp.gene}</span>
        </div>
        <button
          onClick={() => { setMessages([]); setInput(initialPrompt); setLoading(false); }}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Reset chat"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && !loading && (
          <div className="flex items-center justify-center h-full text-center text-gray-500 px-4">
            <div>
              <Sparkles className="w-10 h-10 mx-auto mb-2 text-indigo-400 opacity-50" />
              <p className="text-sm">Click send to ask about <strong>{snp.gene}</strong></p>
              <p className="text-xs text-gray-400 mt-1">Or edit the question below</p>
            </div>
          </div>
        )}
        {messages.map((msg, idx) => (
          <div key={idx} className={'flex ' + (msg.role === 'user' ? 'justify-end' : 'justify-start')}>
            <div className={
              'max-w-[85%] px-3 py-2 rounded-lg text-sm leading-relaxed ' +
              (msg.role === 'user'
                ? 'bg-indigo-600 text-white rounded-br-sm'
                : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm')
            }>
              {msg.role === 'assistant' ? <MarkdownRenderer content={msg.text} /> : msg.text}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-lg rounded-bl-sm px-3 py-2 shadow-sm">
              <div className="flex gap-1 items-center">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="px-3 py-2 bg-white border-t border-gray-200">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder="Ask a follow-up question..."
            className="flex-1 text-sm px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            disabled={loading}
          />
          <button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="p-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Locked AI panel (no API key set) ────────────────────────────────────────
const LockedAIPanel = ({ onSetupKey }) => (
  <div className="flex flex-col items-center justify-center border border-gray-200 rounded-xl bg-gray-50 text-center p-6" style={{ height: '420px' }}>
    <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-slate-100 rounded-xl flex items-center justify-center mb-3">
      <Key className="w-6 h-6 text-indigo-500" />
    </div>
    <h4 className="text-sm font-bold text-gray-800 mb-1">API Key Required</h4>
    <p className="text-xs text-gray-500 mb-4 leading-relaxed max-w-[200px]">
      Ask the AI about this specific variant, its gene, and what it means for you.
    </p>
    <button
      onClick={onSetupKey}
      className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg transition-all shadow-md"
    >
      <Key className="w-3.5 h-3.5" />
      Enter API Key
    </button>
    <p className="text-[10px] text-gray-400 mt-3">Your key · Your usage · Never stored on our servers</p>
  </div>
);

// ─── Detail Modal ─────────────────────────────────────────────────────────────
const SNPDetailModal = ({ snp, onClose }) => {
  const [showAllConditions, setShowAllConditions] = useState(false);
  const [apiKey, setApiKey] = useState(() => getStoredApiKey());
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);

  if (!snp) return null;

  const riskSummary = createRiskSummary(snp);
  const hasGWAS = snp.gwas?.traits?.length > 0;

  const getCategoryStyle = (category) => {
    const styles = {
      'pathogenic':        'bg-red-50    border-red-300    text-red-900',
      'likely-pathogenic': 'bg-orange-50 border-orange-300 text-orange-900',
      'risk':              'bg-yellow-50 border-yellow-300 text-yellow-900',
      'protective':        'bg-green-50  border-green-300  text-green-900',
      'drug-response':     'bg-purple-50 border-purple-300 text-purple-900',
      'uncertain':         'bg-blue-50   border-blue-300   text-blue-900',
      'likely-inactive':   'bg-slate-50  border-slate-200  text-slate-500',
      'unknown':           'bg-gray-50   border-gray-300   text-gray-900',
    };
    return styles[category] || styles.unknown;
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto shadow-2xl">

          {/* Sticky header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900">{snp.rsid}</h2>
              <span className="text-base text-gray-500 font-medium">{snp.gene}</span>
              <span className="text-lg">{riskSummary.icon}</span>
              <span className={'px-2.5 py-0.5 rounded text-xs font-semibold border ' + getCategoryStyle(riskSummary.category)}>
                {riskSummary.riskLevel}
              </span>
              <span className="px-2 py-0.5 bg-slate-100 border border-slate-300 rounded text-xs font-mono font-semibold text-slate-700">
                {snp.genotype}
              </span>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid lg:grid-cols-5 gap-6">

              {/* Left column — clinical detail */}
              <div className="lg:col-span-3 space-y-5">

                {riskSummary.primaryMessage && (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="text-sm text-gray-800">{riskSummary.primaryMessage}</p>
                    {riskSummary.details.length > 0 && (
                      <ul className="mt-2 space-y-0.5">
                        {riskSummary.details.map((d, i) => (
                          <li key={i} className="text-xs text-gray-500">
                            <span className="text-blue-500 mr-1">•</span>{d}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <section>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                    <TrendingUp className="w-4 h-4 text-indigo-600" />
                    Clinical Information
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-3 grid grid-cols-2 gap-3 text-sm">
                    <div><p className="text-xs text-gray-500">Chromosome</p><p className="font-medium text-gray-900">{snp.chromosome}</p></div>
                    <div><p className="text-xs text-gray-500">Position</p><p className="font-medium text-gray-900">{parseInt(snp.position)?.toLocaleString()}</p></div>
                    <div>
                      <p className="text-xs text-gray-500">Your Genotype</p>
                      <p className="font-mono font-bold text-gray-900 text-base">{snp.genotype}</p>
                    </div>
                    <div><p className="text-xs text-gray-500">Clinical Significance</p><p className="font-medium text-gray-900">{snp.clinicalSignificance || '—'}</p></div>
                    {snp.reviewStatus && (
                      <div className="col-span-2">
                        <p className="text-xs text-gray-500">Review Status</p>
                        <p className="text-xs font-medium text-blue-700">{snp.reviewStatus}</p>
                      </div>
                    )}
                  </div>
                </section>

                {snp.description && (
                  <section>
                    <h3 className="text-sm font-semibold text-gray-700 mb-1.5">Description</h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-line">{snp.description}</p>
                    </div>
                  </section>
                )}

                {snp.conditions && (() => {
                  const conditions = snp.conditions.split('|').map(c => c.trim()).filter(c => c && c.toLowerCase() !== 'not specified' && c.toLowerCase() !== 'not provided');
                  const visible = showAllConditions ? conditions : conditions.slice(0, 3);
                  return (
                    <section>
                      <h3 className="text-sm font-semibold text-gray-700 mb-1.5">Associated Conditions</h3>
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-1.5">
                        {visible.map((condition, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
                            <p className="text-sm font-medium text-gray-900 leading-snug">{condition}</p>
                          </div>
                        ))}
                        {conditions.length > 3 && (
                          <button
                            onClick={() => setShowAllConditions(v => !v)}
                            className="mt-1 text-xs text-orange-600 hover:text-orange-800 font-medium transition-colors"
                          >
                            {showAllConditions ? 'Show less' : 'Show ' + (conditions.length - 3) + ' more...'}
                          </button>
                        )}
                      </div>
                    </section>
                  );
                })()}

                {hasGWAS && (
                  <section className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        <BookOpen className="w-4 h-4 text-purple-600" />
                        GWAS Associations
                      </h3>
                      <span className="text-xs text-purple-600 font-medium">
                        {snp.gwas.totalAssociations} total studies
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {snp.gwas.traits.slice(0, 6).map((t, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white text-purple-700 text-xs rounded-full border border-purple-200 font-medium">{t}</span>
                      ))}
                      {snp.gwas.traits.length > 6 && (
                        <span className="px-2 py-0.5 text-purple-500 text-xs font-medium">+{snp.gwas.traits.length - 6} more</span>
                      )}
                    </div>
                    <div className="space-y-2">
                      {snp.gwas.topAssociations.slice(0, 5).map((a, i) => (
                        <div key={i} className="bg-white rounded-lg p-3 border border-purple-100">
                          <div className="flex items-start justify-between gap-2 mb-1.5">
                            <p className="text-xs font-semibold text-gray-900 leading-snug flex-1">{a.trait}</p>
                            <GenotypeBadge copies={a.copiesCarried} />
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            {a.riskAllele && (
                              <span className="text-xs text-purple-700 font-mono font-medium bg-purple-50 px-1.5 py-0.5 rounded">
                                Risk allele: {a.riskAllele.split('-')[1]}
                              </span>
                            )}
                            {a.userEffectiveOdds && (
                              <DirectionBadge direction={a.direction} userEffectiveOdds={a.userEffectiveOdds} orBeta={a.orBeta} trait={a.trait} />
                            )}
                            {a.pValue && (
                              <span className="text-xs text-gray-400">
                                p = {a.pValue < 1e-300 ? '<10-300' : a.pValue.toExponential(1)}
                              </span>
                            )}
                            {a.pubmedId && (
                              <a href={'https://pubmed.ncbi.nlm.nih.gov/' + a.pubmedId + '/'}
                                target="_blank" rel="noopener noreferrer"
                                className="text-xs text-purple-600 hover:text-purple-800 inline-flex items-center gap-0.5 ml-auto">
                                PubMed <ExternalLink className="w-2.5 h-2.5" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                <div className="flex flex-wrap gap-2">
                  {snp.url && (
                    <a href={snp.url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors">
                      ClinVar <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <a href={'https://www.ncbi.nlm.nih.gov/snp/' + snp.rsid} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded-lg transition-colors">
                    dbSNP <ExternalLink className="w-3 h-3" />
                  </a>
                  <a href={'https://www.snpedia.com/index.php/' + snp.rsid} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs rounded-lg transition-colors">
                    SNPedia <ExternalLink className="w-3 h-3" />
                  </a>
                  {hasGWAS && (
                    <a href={'https://www.ebi.ac.uk/gwas/variants/' + snp.rsid} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-lg transition-colors">
                      GWAS Catalog <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>

              {/* Right column — AI chat */}
              <div className="lg:col-span-2">
                {apiKey
                  ? <AIChatPanel snp={snp} apiKey={apiKey} />
                  : <LockedAIPanel onSetupKey={() => setShowApiKeyModal(true)} />
                }
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-800">
                <strong>Educational use only.</strong> Genetic variants interact with many factors. Consult a genetic counselor for personalized medical advice.
              </p>
            </div>
          </div>
        </div>
      </div>

      {showApiKeyModal && (
        <APIKeyModal
          onClose={() => setShowApiKeyModal(false)}
          onKeySaved={(k) => setApiKey(k)}
        />
      )}
    </>
  );
};

export default SNPDetailModal;